import { useEffect, useState, useCallback } from 'react';
import NewGameState from '../_types/NewGameState';
import { useGetUserInformationQuery } from '../../../../store/api/GraphQlQuery';
import saveGame from '../../games-helpers/saveGame';
import { Storage, API } from 'aws-amplify';
import { GamifyToast } from '../../../../common/CustomToasts';
import { isBefore } from 'date-fns';
import { useQueryClient, QueryKey } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

const INITIAL_STATE: NewGameState = {
  id: '',
  type: 'mission',
  name: '',
  description: '',
  contestType: 'regular',
  scope: 'person',
  isCircleOnly: false,
  isPublic: false,
  startDate: '',
  endDate: '',
  useMultipleKPIs: false,
  KPIRuleID: '',
  KPIRuleName: '',
  tieKPIRuleID: '',
  tieKPIRuleName: '',
  actionCount: '0',
  isActionCountCustom: false,
  assigneeIDs: [],
  rewardDisposition: 'winner',
  rewardWhen: 'mission',
  rewardType: 'mission_reward',
  rewardCurrency: 'reward',
  // Incentive defaults
  objectiveRewardPolicy: 'per_objective',
  awardTiming: 'mission',
  incentive: { objectives: [] },
  rewardID1: '',
  rewardName1: '',
  rewardID2: '',
  rewardName2: '',
  rewardID3: '',
  rewardName3: '',
  rewardIDp: '',
  rewardNameP: '',
  rewardID1t: '',
  rewardName1t: '',
  rewardID2t: '',
  rewardName2t: '',
  rewardID3t: '',
  rewardName3t: '',
  rewardIDpt: '',
  rewardNamePt: '',
  showCustomReward1: false,
  showCustomReward2: false,
  showCustomReward3: false,
  rewardLimit: '',
  imageName: '',
  imageType: '',
  imageFile: null,
  advanceType: true,
  automaticallyCreateGoals: true,
  createRecurringCampaigns: true,
  missionRules: [],
  rules: [],
  autoGoal: '',
  tags: [],
  action: '',
  actionCountType: 'count',
  actionCountTypeNum: 0,
  actionPastTense: '',
  actionType: '',
  actionTypePlural: '',
  assignToChildCircles: true,
  autoAssign: true,
  autoCreateEndDate: '',
  autoCreateStartDate: '',
  autoCreateTimeframe: 'W',
  autoCreateTimeframeCount: '1',
  autoGoalFactor: '100',
  autoGoalTimeframe: 'W',
  autoGoalTimeframeCount: '4',
  bracket: {
    roundValues: [],
    bracket: [],
    totalCompetitors: 2,
    roundSpecificRules: true,
    roundOver: 'after_time',
    nextRoundStarts: 'immediately',
    daysBeforeNextRound: 0,
    daysInRound: 0,
  },
  bracketName: '',
  categoryID: '',
  competitors: [],
  copiedMissionID: '',
  copiedMissionName: '',
  copyMission: false,
  createMission: false,
  flexFields: [],
  isDeleted: false,
  launchDate: '',
  location: '',
  managerPlay: true,
  minAutoGoal: '1.0',
  minGoal: '',
  missionRewardID1: '',
  missionRewardID1t: '',
  missionRewardID2: '',
  missionRewardID2t: '',
  missionRewardID3: '',
  missionRewardID3t: '',
  missionRewardIDp: '',
  missionRewardIDpt: '',
  nextMissionID: '',
  orgID: '',
  ownerID: '',
  ownerType: '',
  parentID: '',
  parentName: '',
  pickAssignee: false,
  questOrder: '',
  questType: '',
  rewardCount1: '',
  rewardCount1t: '',
  rewardCount2: '',
  rewardCount2t: '',
  rewardCount3: '',
  rewardCount3t: '',
  rewardCountP: '',
  roundGoalPrecision: '',
  search: '',
  topNumberAdvance: 0,
  trophies: [],
  tzOffset: -8,
  useAutoCreate: false,
  useAutoGoal: false,
  useDealStage: true,
  useStepCounter: false,
  totalRounds: 0,
  currentRound: 0,
};

// GraphQL query to get a single game for editing
const getLeaderboardsQuery = /* GraphQL */ `
  query GetLeaderboards($request: AWSJSON!) {
    getLeaderboards(request: $request)
  }
`;

const useNewGame = () => {
  const { data: userData, isLoading: loadingGetUserInformation } =
    useGetUserInformationQuery({});
  const [newGameState, setNewGameState] = useState<NewGameState>(INITIAL_STATE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const [errorMessages, setErrorMessages] = useState({
    name: '',
    startDate: '',
    endDate: '',
  });
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();

  const isErrorPresent = Object.values(errorMessages).some(
    (message) => message !== ''
  );
  const [shouldDisableNextOrFinishButton, setShouldDisableNextOrFinishButton] =
    useState(isErrorPresent);

  const onChangeNewGameState = (newState: NewGameState) => {
    setNewGameState(newState);
  };

  // Function to load a game for editing - memoized
  const loadGameForEditing = useCallback(async (gameId: string) => {
    if (!gameId) return;

    try {
      setLoading(true);

      const response: any = await API.graphql({
        query: getLeaderboardsQuery,
        variables: {
          request: JSON.stringify({
            missionID: gameId,
          }),
        },
      });

      const jsonRes = JSON.parse(response?.data?.getLeaderboards);
      console.log('🚀 ~ loadGameForEditing ~ jsonRes:', jsonRes);
      const gameData = jsonRes.Items?.[0];

      if (gameData) {
        // Extract contestant information from users array
        const userItems = gameData.users?.items || [];
        const assigneeIDs: string[] = [];
        const assigneeNames: string[] = [];
        const competitors: Array<{ id: string; name: string; avatar: string }> =
          [];

        if (userItems.length > 0) {
          // Process each user in the mission
          userItems.forEach((userItem: any) => {
            if (
              userItem.user &&
              userItem.status === 'accepted' &&
              !userItem.isDeleted
            ) {
              const user = userItem.user;
              const userId = user.id;
              const userName = user.name;

              // Add to arrays
              assigneeIDs.push(userId);
              assigneeNames.push(userName);

              // Create competitor object with properly typed fields
              competitors.push({
                id: userId,
                name: userName,
                avatar: user.imageName ? user.imageName : '',
              });
            }
          });
        }

        // Extract reward information from rewards.items array
        const rewardItems = gameData.rewards?.items || [];
        let rewardID1 = '';
        let rewardName1 = '';
        let rewardID2 = '';
        let rewardName2 = '';
        let rewardID3 = '';
        let rewardName3 = '';
        let rewardIDp = ''; // Participation reward
        let rewardNameP = '';

        // Process each reward in the mission
        rewardItems.forEach((rewardItem: any) => {
          if (!rewardItem.isDeleted && rewardItem.reward) {
            const reward = rewardItem.reward;
            const place = rewardItem.place;

            // Map rewards to the correct fields based on place
            switch (place) {
              case 1:
                rewardID1 = reward.id;
                rewardName1 = reward.name;
                break;
              case 2:
                rewardID2 = reward.id;
                rewardName2 = reward.name;
                break;
              case 3:
                rewardID3 = reward.id;
                rewardName3 = reward.name;
                break;
              case 4: // Assume place 4 is for participation
                rewardIDp = reward.id;
                rewardNameP = reward.name;
                break;
            }
          }
        });

        // Determine reward currency based on reward_type or rewardType
        let rewardCurrency = 'reward'; // Default to 'reward'

        if (gameData.reward_type === 'mission_points') {
          rewardCurrency = 'points';
        } else if (gameData.rewardType === 'points') {
          rewardCurrency = 'points';
        }

        // Extract and standardize rules
        const gameRules = gameData.rules || gameData.missionRules || [];

        // Check if the game has KPIs or rules that would indicate multiple KPIs
        const detectMultipleKPIs = () => {
          // Already explicitly set with useMultipleKPIs flag
          if (gameData.useMultipleKPIs) {
            console.log(
              '[KPI-DEBUG] Multiple KPIs detected from useMultipleKPIs flag'
            );
            return true;
          }

          // Has rules populated which indicates multiple KPIs
          if (gameRules.length > 0) {
            console.log(
              '[KPI-DEBUG] Multiple KPIs detected from rules array',
              gameRules
            );
            return true;
          }

          // Having both a primary KPI and tie KPI does NOT mean multiple KPIs
          // This is a separate feature - tie KPI is available regardless of multiple KPIs

          console.log('[KPI-DEBUG] No multiple KPIs detected');
          return false;
        };

        const hasMultipleKPIs = detectMultipleKPIs();

        // Parse bracket data if this is a bracket game
        let bracketData = {};
        let parsedCompetitors: any[] = [...competitors];

        if (gameData.contestType && gameData.contestType.includes('bracket')) {
          try {
            // If bracket is a string, parse it; otherwise use as is
            const parsedBracket =
              typeof gameData.bracket === 'string'
                ? JSON.parse(gameData.bracket)
                : gameData.bracket;

            // Process round values if they exist
            if (
              parsedBracket?.roundValues &&
              parsedBracket.roundValues.length > 0
            ) {
              parsedBracket.roundValues = parsedBracket.roundValues.map(
                (round: any) => {
                  // Get this round's rules or use the game's rules
                  const roundRules =
                    round.rules || round.missionRules || gameRules;

                  return {
                    ...round,
                    useMultipleKPIs: hasMultipleKPIs,
                    rules: roundRules,
                    missionRules: roundRules,
                  };
                }
              );
            }

            bracketData = {
              ...parsedBracket,
              totalCompetitors:
                parsedBracket?.totalCompetitors || competitors.length || 0,
            };

            // For bracket games with competitor data
            if (gameData.competitors && gameData.competitors.length > 0) {
              // If competitors is an array of strings (IDs), use it directly for bracket mode
              if (typeof gameData.competitors[0] === 'string') {
                parsedCompetitors = gameData.competitors;
              }
            } else if (assigneeIDs.length > 0) {
              // If no competitors but we have assigneeIDs, use those
              parsedCompetitors = assigneeIDs;
            }
          } catch (error) {
            console.error('Error parsing bracket data:', error);
            // Default empty bracket
            bracketData = {
              roundValues: [],
              bracket: [],
              totalCompetitors: competitors.length || 0,
              roundSpecificRules: true,
              roundOver: 'after_time',
              nextRoundStarts: 'immediately',
              daysBeforeNextRound: 0,
              daysInRound: 0,
            };
          }
        }

        // Create the final state (without actually setting it yet)
        const finalState = {
          ...gameData,
          id: gameData.id,
          name: gameData.name || '',
          description: gameData.description || '',
          contestType: gameData.contestType || 'regular',
          scope: gameData.scope || 'person',
          startDate: gameData.startDate || '',
          endDate: gameData.endDate || '',
          assigneeIDs,
          assigneeNames,
          rewardWhen: gameData.rewardWhen || 'mission',
          rewardCurrency,
          rewardType: gameData.rewardType || 'mission_reward',
          rewardDisposition: gameData.rewardDisposition || 'winner',
          // Set reward IDs and names
          rewardID1,
          rewardName1,
          rewardID2,
          rewardName2,
          rewardID3,
          rewardName3,
          rewardIDp,
          rewardNameP,
          // Other reward-related fields
          KPIRuleID: gameData.KPIRuleID || '',
          KPIRuleName: gameData.KPIRuleName || '',
          tieKPIRuleID: gameData.tieKPIRuleID || '',
          tieKPIRuleName: gameData.tieKPIRuleName || '',
          useMultipleKPIs: hasMultipleKPIs,
          rules: gameRules,
          missionRules: gameRules,
          imageName: gameData.imageName || '',
          imageType: gameData.imageType || '',
          // Set bracket related data
          bracket: gameData.contestType?.includes('bracket')
            ? bracketData
            : null,
          competitors: parsedCompetitors,
          bracketName: gameData.bracketName || '',
        };

        // Log the final state we're about to set
        console.log('[KPI-EDIT-DEBUG] Final processed state:', {
          gameId: finalState.id,
          gameName: finalState.name,
          contestType: finalState.contestType,
          KPI: {
            KPIRuleID: finalState.KPIRuleID,
            KPIRuleName: finalState.KPIRuleName,
            tieKPIRuleID: finalState.tieKPIRuleID,
            tieKPIRuleName: finalState.tieKPIRuleName,
            useMultipleKPIs: finalState.useMultipleKPIs,
          },
          hasRules: Boolean(finalState.rules?.length),
          rulesCount: finalState.rules?.length || 0,
          rulesSample: finalState.rules?.slice(0, 2) || [],
          hasMissionRules: Boolean(finalState.missionRules?.length),
          missionRulesCount: finalState.missionRules?.length || 0,
          missionRulesSample: finalState.missionRules?.slice(0, 2) || [],
          isBracket: finalState.contestType?.includes('bracket'),
          bracketRounds: finalState.bracket?.roundValues?.length || 0,
        });

        // Actually set the state
        setNewGameState((prevState) => ({
          ...prevState,
          ...finalState,
        }));

        // Log KPI-related data from the loaded game
        console.log('[KPI-EDIT-DEBUG] Game data loaded:', {
          gameId: gameData.id,
          gameName: gameData.name,
          contestType: gameData.contestType,
          KPI: {
            KPIRuleID: gameData.KPIRuleID,
            KPIRuleName: gameData.KPIRuleName,
            tieKPIRuleID: gameData.tieKPIRuleID,
            tieKPIRuleName: gameData.tieKPIRuleName,
            useMultipleKPIs: gameData.useMultipleKPIs,
          },
          hasRules: Boolean(gameData.rules?.length),
          rulesCount: gameData.rules?.length || 0,
          rulesSample: gameData.rules?.slice(0, 2) || [],
          hasMissionRules: Boolean(gameData.missionRules?.length),
          missionRulesCount: gameData.missionRules?.length || 0,
          missionRulesSample: gameData.missionRules?.slice(0, 2) || [],
          isBracket: gameData.contestType?.includes('bracket'),
          bracketRounds: gameData.bracket?.roundValues?.length || 0,
        });
      }
    } catch (error) {
      console.error('Error loading game for editing:', error);
      setError(error);
      GamifyToast.error('Failed to load game data for editing');
    } finally {
      setLoading(false);
    }
  }, []);

  const onSaveGame = async () => {
    try {
      setLoading(true);
      setError(null);

      // Format the game data before saving
      const formattedGameData = { ...newGameState };
      const isManualBracketGame =
        formattedGameData.contestType === 'bracket_manual';

      // Ensure assigneeIDs and competitors are in sync
      if (
        formattedGameData.competitors &&
        formattedGameData.competitors.length > 0
      ) {
        // Update assigneeIDs based on the current competitors list
        // Handle both cases: competitors can be array of objects or array of IDs
        formattedGameData.assigneeIDs = formattedGameData.competitors.map(
          (competitor: any) =>
            typeof competitor === 'string'
              ? competitor
              : competitor.id || competitor.value
        );

        // Initialize assigneeNames if not already present
        if (!formattedGameData.assigneeNames) {
          formattedGameData.assigneeNames = [];
        }

        // Update assigneeNames if we have access to the names
        // Only try to extract names if competitors contains objects with name property
        if (typeof formattedGameData.competitors[0] !== 'string') {
          formattedGameData.assigneeNames = formattedGameData.competitors.map(
            (competitor: any) => competitor.name || competitor.label
          );
        }

        // Set the first contestant as the assigneeID if not set
        if (
          !formattedGameData.assigneeID &&
          formattedGameData.assigneeIDs.length > 0
        ) {
          formattedGameData.assigneeID = formattedGameData.assigneeIDs[0];
          // Only set assigneeName if we have names available
          if (
            formattedGameData.assigneeNames &&
            formattedGameData.assigneeNames.length > 0
          ) {
            formattedGameData.assigneeName = formattedGameData.assigneeNames[0];
          }
        }
      } else if (
        formattedGameData.assigneeIDs &&
        formattedGameData.assigneeIDs.length > 0
      ) {
        // If we have assigneeIDs but no competitors
        // For bracket games, we need to set competitors array with IDs
        if (formattedGameData.contestType?.includes('bracket')) {
          formattedGameData.competitors = [...formattedGameData.assigneeIDs];
        } else {
          // For non-bracket games, set competitors to empty array
          formattedGameData.competitors = [];
        }
      } else {
        // Reset both arrays if empty
        formattedGameData.assigneeIDs = [];
        formattedGameData.assigneeNames = [];
        formattedGameData.competitors = [];
      }

      // 2. Set launchDate to be the same as startDate
      if (formattedGameData.startDate) {
        formattedGameData.launchDate = formattedGameData.startDate;
      }

      // Format dates to ensure proper serialization in GraphQL
      if (
        formattedGameData.startDate &&
        typeof formattedGameData.startDate === 'string'
      ) {
        formattedGameData.startDate = new Date(
          formattedGameData.startDate
        ).toISOString();
      }
      if (
        formattedGameData.endDate &&
        typeof formattedGameData.endDate === 'string'
      ) {
        formattedGameData.endDate = new Date(
          formattedGameData.endDate
        ).toISOString();
      }
      if (
        formattedGameData.launchDate &&
        typeof formattedGameData.launchDate === 'string'
      ) {
        formattedGameData.launchDate = new Date(
          formattedGameData.launchDate
        ).toISOString();
      }

      // 3. Add orgID from global.me.orgID - safely access it
      try {
        // @ts-ignore - Access global object safely
        if (global && global.me && global.me.orgID) {
          // @ts-ignore
          formattedGameData.orgID = global.me.orgID;
        } else {
          // Default to current orgID or empty string if not available
          formattedGameData.orgID = formattedGameData.orgID || '';
        }
      } catch (e) {
        // If any error occurs accessing global.me, use existing orgID or empty string
        formattedGameData.orgID = formattedGameData.orgID || '';
      }

      // 4. Set questOrder and questType to "notaquest"
      formattedGameData.questOrder = 'notaquest';
      formattedGameData.questType = 'notaquest';

      // 5. Set the reward_type based on rewardCurrency
      formattedGameData.reward_type =
        formattedGameData.rewardCurrency === 'points'
          ? 'mission_points'
          : 'mission_reward';

      if (formattedGameData.imageFile) {
        await Storage.put(
          formattedGameData.imageFile.name,
          formattedGameData.imageFile,
          {
            contentType: formattedGameData.imageFile.type,
          }
        );
      }

      // TODO: Remove this once we have a way to handle bracket seed games
      if (isManualBracketGame) {
        // we need to delete the bracket.bracket array since we're now using the competitors array
        formattedGameData.bracket.bracket = [];
        // For manual bracket games, ensure competitors contains the assigneeIDs
        formattedGameData.competitors = [...formattedGameData.assigneeIDs];
      }

      // Copy missionRules to rules to ensure consistency with backend
      if (
        formattedGameData.missionRules &&
        formattedGameData.contestType !== 'incentive'
      ) {
        formattedGameData.rules = formattedGameData.missionRules;
      }

      // Ensure incentive fields are passed when incentive type
      if (formattedGameData.contestType === 'incentive') {
        formattedGameData.objectiveRewardPolicy =
          formattedGameData.objectiveRewardPolicy || 'per_objective';
        formattedGameData.awardTiming =
          formattedGameData.awardTiming || 'mission';
        formattedGameData.rewardWhen =
          formattedGameData.awardTiming === 'mission' ? 'mission' : 'mission';
        // incentive structure already maintained in state via Incentive UI
      }

      // Handle bracket games - ensure each round has rules copied from missionRules
      if (formattedGameData.bracket && formattedGameData.bracket.roundValues) {
        formattedGameData.bracket.roundValues =
          formattedGameData.bracket.roundValues.map((round) => {
            if (round.missionRules) {
              return { ...round, rules: round.missionRules };
            }
            return round;
          });
      }

      // Final check to ensure correct format for competitors based on game type
      if (formattedGameData.contestType?.includes('bracket')) {
        // For bracket games, ensure competitors contains only IDs
        formattedGameData.competitors = [...formattedGameData.assigneeIDs];
      } else {
        // For non-bracket games, competitors should be empty
        formattedGameData.competitors = [];
      }

      console.log('🚀 ~ onSaveGame ~ formattedGameData:', formattedGameData);
      console.log('🚀 ~ onSaveGame ~ formattedGameData rules:', {
        missionRules: formattedGameData?.missionRules,
        rules: formattedGameData?.rules,
      });
      // return;

      const newGame = await saveGame(formattedGameData);

      // Clear all "game" cache entries immediately after successful save
      const cacheKeys = queryClient
        .getQueryCache()
        .getAll()
        .map((query) => query.queryKey);
      const gameKeys = cacheKeys.filter(
        (key): key is QueryKey => Array.isArray(key) && key[0] === 'game'
      );

      console.log('Found game keys to clear:', gameKeys);

      // Explicitly remove each game key from cache
      gameKeys.forEach((key) => {
        console.log('Removing query cache for key:', key);
        queryClient.removeQueries({ queryKey: key });
      });

      // Explicitly clear the cache for this specific game ID
      const gameId = newGameState.id || newGame?.id;
      if (gameId) {
        console.log(`Explicitly removing cache for game ID: ${gameId}`);
        queryClient.removeQueries({ queryKey: ['game', gameId] });
      }

      // Force React Query to update any observers
      queryClient.invalidateQueries({ queryKey: ['games'] });

      console.log('Cache cleared for game keys after save');

      // Show success toast with improved messaging
      const isBracketGame = formattedGameData.contestType?.includes('bracket');
      if (isBracketGame) {
        GamifyToast.success(
          newGameState?.id
            ? 'Game updated successfully. Bracket data may take a moment to reflect changes.'
            : 'Game created successfully. Bracket data may take a moment to load completely.'
        );
      } else {
        GamifyToast.success(
          newGameState?.id
            ? 'Game updated successfully. Please wait while we refresh the data.'
            : 'Game created successfully.'
        );
      }

      // Check if we're on a game details page and if we're editing an existing game
      const isGameDetailsPage =
        location.pathname.includes('/performance/games/') &&
        location.pathname.split('/').length > 3;
      const isEditingExistingGame = Boolean(newGameState.id);

      // Reset the game state to initial state after successful save
      resetNewGameState();

      // If we're editing from the game details page, navigate back to games list
      if (isGameDetailsPage && isEditingExistingGame) {
        const isV2 = location.pathname.startsWith('/v2');
        setTimeout(() => {
          navigate(`${isV2 ? '/v2' : ''}/performance/games`);
        }, 500);
      }

      return newGame;
    } catch (error: any) {
      console.error('Error in onSaveGame:', error);
      setError(error);
      GamifyToast.error(
        'Save Failed',
        error?.message || 'Something went wrong saving the game'
      );
      throw error; // Re-throw to handle in the component
    } finally {
      setLoading(false);
    }
  };
  const resetNewGameState = () => {
    setNewGameState(INITIAL_STATE);
  };

  useEffect(() => {
    const initializeState = () => {
      if (loadingGetUserInformation || !userData) {
        return;
      }

      setNewGameState((prevState) => ({
        ...prevState,
        rewardCurrency: userData?.appSettings?.entitlements?.includes(
          'mission:reward:reward'
        )
          ? 'reward'
          : 'points',
      }));
    };

    initializeState();
  }, [loadingGetUserInformation]);

  useEffect(() => {
    const errors = validate();
    const isErrorPresent = Object.values(errors).some(
      (message) => message !== ''
    );

    setShouldDisableNextOrFinishButton(
      loading || !newGameState.name || isErrorPresent
    );
    setErrorMessages(errors);
  }, [
    loading,
    newGameState.name,
    newGameState.startDate,
    newGameState.endDate,
  ]);

  const validate = () => {
    const errors = { ...errorMessages };

    // Name validation
    if (newGameState.name.length < 3 && newGameState.name.length !== 0) {
      errors.name = 'Name should be at least 3 characters long';
    } else {
      errors.name = '';
    }

    // Start Date validation - Temporarily disabled for editing games
    // if (
    //   newGameState.startDate &&
    //   isBefore(new Date(newGameState.startDate), new Date())
    // ) {
    //   errors.startDate = 'Start Date cannot be earlier than the current time';
    // } else {
    //   errors.startDate = '';
    // }
    errors.startDate = '';

    // End Date validation
    if (
      newGameState.endDate &&
      newGameState.startDate &&
      isBefore(new Date(newGameState.endDate), new Date(newGameState.startDate))
    ) {
      errors.endDate = 'End Date should not be earlier than Start Date';
    } else {
      errors.endDate = '';
    }

    return errors;
  };

  return {
    loading,
    error,
    newGameState,
    onChangeNewGameState,
    onSaveGame,
    shouldDisableNextOrFinishButton,
    setShouldDisableNextOrFinishButton,
    errorMessages,
    loadGameForEditing,
    resetNewGameState,
  };
};

export default useNewGame;
